import { Star, CheckCircle2, MapPin, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n";

export const StarRating = ({ value, className = "" }) => (
  <span className={`inline-flex items-center gap-1 ${className}`}>
    <Star className="w-4 h-4 fill-[#E69625] text-[#E69625]" />
    <span className="font-semibold text-[#3A2E28]">{Number(value).toFixed(1)}</span>
  </span>
);

export const CraftsmanCard = ({ c, index = 0 }) => {
  const { t, catName, cityName, jt } = useI18n();
  const initial = (c.name || "?").trim().charAt(0).toUpperCase();
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay: index * 0.06 }}
    >
      <Link
        to={`/craftsmen/${c._id}`}
        data-testid={`craftsman-card-${c._id}`}
        className="group relative block rounded-2xl bg-white overflow-hidden border border-[#EBDDD3] shadow-sm hover:shadow-[0_24px_50px_-20px_rgba(45,35,31,0.35)] hover:-translate-y-1.5 transition-[transform,box-shadow] duration-300"
      >
        <div className="relative h-52 overflow-hidden bg-gradient-to-br from-[#C65338] to-[#8f3b26]">
          {c.avatar ? (
            <img
              src={c.avatar}
              alt={c.name}
              className="w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-[900ms] ease-out"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <span className="font-display text-7xl font-black text-white/90">{initial}</span>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

          {c.verified && (
            <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-[#5C6646] text-white text-[11px] font-bold px-2.5 py-1 shadow-md">
              <CheckCircle2 className="w-3.5 h-3.5" /> {t("craftsmen.verified")}
            </span>
          )}

          <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-white/95 backdrop-blur px-2.5 py-1 shadow-md text-xs">
            <Star className="w-3.5 h-3.5 fill-[#E69625] text-[#E69625]" />
            <span className="font-black text-[#2D231F]">{Number(c.rating).toFixed(1)}</span>
          </span>

          <div className="absolute bottom-0 left-0 right-0 p-4">
            <h3 className="font-display font-black text-xl text-white leading-tight drop-shadow-sm">{c.name}</h3>
            <div className="flex items-center gap-2 mt-1.5">
              <span className="px-2 py-0.5 rounded-full bg-white/20 backdrop-blur text-white font-semibold text-[11px]">
                {catName(c.trade) || c.trade}
              </span>
              {c.city && (
                <span className="inline-flex items-center gap-1 text-white/90 text-xs">
                  <MapPin className="w-3 h-3" />{cityName(c.city)}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="p-5">
          <p className="text-sm text-[#8C7A70] line-clamp-2 min-h-[2.5rem]">{jt(c, "bio")}</p>
          <div className="flex items-center justify-between pt-4 mt-3 border-t border-[#F2E8DF]">
            <span className="text-xs text-[#A89A90]">{c.reviews} {t("profile.reviews")}</span>
            <span className="inline-flex items-center gap-1 text-sm font-bold text-[#C65338] group-hover:gap-2 transition-all">
              {t("craftsmen.viewProfile")} <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};
